import { useState } from 'react';

import { Trans, useLingui } from '@lingui/react/macro';
import { Switch } from '@repo/videa-components/src/ui/switch';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

import type {
  GetClaimSummaryResponse,
  TTreatmentResponse,
} from '@/modules/claims/freeClaims/insuranceExport/types';
import {
  cleanClaimsAttachmentValuesAtom,
  clinicalNotesAtom,
  excludeFmxFindingsWhenPeriochartPresentAtom,
  isNarrativeLoadingAtom,
  narrativeAtom,
  selectedTreatmentCodesAtom,
  selectedTreatmentsAtom,
} from '@/modules/claims/freeClaims/state';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { ClaimNarrative } from './ClaimNarrative';
import { ClinicalNote } from './ClinicalNote';
import { ConfigurationItemSection } from './ConfigurationItemSection';
import { SelectServices } from './SelectServices';

type ConfigurationsProps = {
  defaultTreatments: TTreatmentResponse[];
  onSelectTreatments: (txIds: string[]) => void;
  claimSummary?: GetClaimSummaryResponse;
};

export const Configurations = ({
  defaultTreatments,
  onSelectTreatments,
  claimSummary,
}: ConfigurationsProps) => {
  // atoms
  const cleanClaimsAttachmentValues = useAtomValue(
    cleanClaimsAttachmentValuesAtom,
  );
  const selectedTreatments = useAtomValue(selectedTreatmentsAtom) ?? [];
  const narrativeText = useAtomValue(narrativeAtom);
  const clinicalNotes = useAtomValue(clinicalNotesAtom);
  const selectedTreatmentCodes = useAtomValue(selectedTreatmentCodesAtom);
  const isNarrativeLoading = useAtomValue(isNarrativeLoadingAtom);
  const [
    excludeFmxFindingsWhenPeriochartPresent,
    setExcludeFmxFindingsWhenPeriochartPresent,
  ] = useAtom(excludeFmxFindingsWhenPeriochartPresentAtom);

  const setNarrativeAtomText = useSetAtom(narrativeAtom);
  const setClinicalNotes = useSetAtom(clinicalNotesAtom);

  // state
  const [isEditingNarrative, setIsEditingNarrative] = useState(false);
  const [isEditingClinicalNote, setIsEditingClinicalNote] = useState(false);
  const [isEditingServices, setIsEditingServices] = useState(false);
  const [narrativeTextDraft, setNarrativeTextDraft] = useState(narrativeText);
  const [clinicalNotesDraft, setClinicalNotesDraft] = useState(clinicalNotes);
  const [selectedTreatmentsDraft, setSelectedTreatmentsDraft] =
    useState(selectedTreatments);

  // hooks
  const { t } = useLingui();
  const { trackEvent } = useTrackEvent();

  const { unifiedId, estimatedVisitId = '' } = cleanClaimsAttachmentValues;

  // Helper function to derive treatment codes from treatment IDs
  const getTreatmentCodes = (treatmentIds: string[]) => [
    ...new Set(
      treatmentIds
        .map(
          (txId) =>
            defaultTreatments.find((t) => t.txId.toString() === txId)?.txCode,
        )
        .filter(Boolean),
    ),
  ];

  // Narrative handlers
  const handleNarrativeEdit = () => {
    trackEvent(mixpanelEvents.editClaimNarrative, {
      unifiedId,
      estimatedVisitId,
      txCode: selectedTreatmentCodes,
    });
    setIsEditingNarrative(true);
  };

  const handleNarrativeCancel = () => {
    setIsEditingNarrative(false);
    setNarrativeTextDraft(narrativeText);
  };

  const handleNarrativeSave = (text: string) => {
    trackEvent(mixpanelEvents.saveClaimNarrative, {
      unifiedId,
      estimatedVisitId,
      txCode: selectedTreatmentsDraft,
    });
    setNarrativeAtomText(text.trim() === '' ? 'None provided.' : text);
    setIsEditingNarrative(false);
  };

  // Clinical note handlers
  const handleClinicalNoteEdit = () => {
    trackEvent(mixpanelEvents.editClinicalNote, {
      unifiedId,
      estimatedVisitId,
      txCode: selectedTreatmentCodes,
    });
    setIsEditingClinicalNote(true);
  };

  const handleClinicalNoteCancel = () => {
    setIsEditingClinicalNote(false);
    setClinicalNotesDraft(clinicalNotes);
  };

  const handleClinicalNoteSave = (text: string) => {
    trackEvent(mixpanelEvents.saveClinicalNote, {
      unifiedId,
      estimatedVisitId,
      txCode: selectedTreatmentCodes,
    });
    setClinicalNotes(text);
    setIsEditingClinicalNote(false);
  };

  // Services handlers
  const handleServicesEdit = () => {
    trackEvent(mixpanelEvents.editAttachmentServices, {
      unifiedId,
      estimatedVisitId,
      txCode: selectedTreatmentCodes,
    });
    setSelectedTreatmentsDraft(selectedTreatments);
    setIsEditingServices(true);
  };

  const handleServicesCancel = () => {
    setIsEditingServices(false);
  };

  const handleServicesSave = () => {
    onSelectTreatments(selectedTreatmentsDraft);
    setIsEditingServices(false);
    trackEvent(mixpanelEvents.saveAttachmentServices, {
      unifiedId,
      estimatedVisitId,
      txCode: getTreatmentCodes(selectedTreatmentsDraft),
    });
  };

  const hasPeriochart =
    claimSummary?.perioChart?.teeth && claimSummary.perioChart.teeth.length > 0;

  return (
    <div className="vai-flex vai-flex-col vai-gap-4">
      <h2 className="vai-font-semibold vai-text-lg">
        <Trans>Configurations</Trans>
      </h2>
      <div className="vai-flex vai-flex-col">

        {/* Toggle to exclude FMX findings when periochart is present */}
        {hasPeriochart && (
          <div className="vai-flex vai-items-center vai-justify-between vai-bg-gray-100 vai-py-2 vai-px-4 vai-rounded-md vai-mt-4 vai-mb-4">
            <div className="vai-flex vai-flex-col vai-gap-1">
              <label
                htmlFor="exclude-fmx-findings"
                className="vai-font-medium vai-text-gray-900 vai-cursor-pointer"
              >
                <Trans>Exclude FMX Findings</Trans>
              </label>
              <p className="vai-text-xs vai-text-gray-600">
                <Trans>
                  Hide FMX findings table when periochart is present
                </Trans>
              </p>
            </div>
            <Switch
              id="exclude-fmx-findings"
              checked={excludeFmxFindingsWhenPeriochartPresent}
              onCheckedChange={setExcludeFmxFindingsWhenPeriochartPresent}
            />
          </div>
        )}

        <ConfigurationItemSection
          dataCy="services"
          title={t`Select Services`}
          isEditing={isEditingServices}
          onEdit={handleServicesEdit}
          onSave={
            // If the selected treatments are the same, disable Save button
            selectedTreatmentsDraft.length === selectedTreatments.length &&
              selectedTreatmentsDraft.every((treatment) =>
                selectedTreatments.includes(treatment),
              )
              ? undefined
              : handleServicesSave
          }
          onCancel={handleServicesCancel}
        >
          <SelectServices
            defaultTreatments={defaultTreatments}
            selectedTreatmentsDraft={selectedTreatmentsDraft}
            isEditing={isEditingServices}
            onSetSelectedTreatmentsDraft={setSelectedTreatmentsDraft}
          />
        </ConfigurationItemSection>

        <ConfigurationItemSection
          dataCy="narrative"
          title={t`Claim Narrative`}
          isEditing={isEditingNarrative}
          onEdit={handleNarrativeEdit}
          onSave={
            // If the narrative text is the same, disable Save button
            narrativeTextDraft !== narrativeText
              ? () => handleNarrativeSave(narrativeTextDraft)
              : undefined
          }
          onCancel={handleNarrativeCancel}
          disabled={isNarrativeLoading}
        >
          <ClaimNarrative
            narrativeTextDraft={narrativeTextDraft}
            isEditing={isEditingNarrative}
            onChange={setNarrativeTextDraft}
          />
        </ConfigurationItemSection>

        <ConfigurationItemSection
          dataCy="clinical-note"
          title={t`Clinical Note`}
          isEditing={isEditingClinicalNote}
          onEdit={handleClinicalNoteEdit}
          onSave={
            // If the clinical notes are the same, disable Save button
            clinicalNotesDraft !== clinicalNotes
              ? () => handleClinicalNoteSave(clinicalNotesDraft)
              : undefined
          }
          onCancel={handleClinicalNoteCancel}
        >
          <ClinicalNote
            isEditing={isEditingClinicalNote}
            clinicalNotesDraft={clinicalNotesDraft}
            onChange={setClinicalNotesDraft}
          />
        </ConfigurationItemSection>
      </div>
    </div>
  );
};
